// A user role as the mock-up draws it: one panel, five tabs — the role itself,
// then the four permission screens that used to be four separate popups.
//
// Each tab still talks to its own endpoint with its own payload: the profile
// posts a UserRole, the menu tabs post a WebUserMenuPermission, the special
// tabs post a UserSpecialPermission with the same
// `userRole.id !== 0` filter, and the same redux refreshes fire afterwards.
// The Save button in the footer saves the tab you are looking at, which is why
// it names it.
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { UserRole } from "../../../core/json/UserRole";
import { WebUserMenuPermission } from "../../../core/json/WebUserMenuPermission";
import { UserSpecialPermission } from "../../../core/json/UserSpecialPermission";
import {
  adduserRole,
  updateuserRole,
  getwebmenupermission,
  savewebmenupermission,
  getwebmenuspecialpermission,
  savewebspecialpermission,
  getwindowmenupermission,
  savewindowmenupermission,
  getwindowmenuspecialpermission,
  savewindowspecialpermission,
} from "../../../services/AdminApiServices";
import { fetchUserRole } from "../../../redux/userSetting";
import { fetchMenuServices } from "../../../redux/menuServices";
import { fetchmenuPermissions, fetchspecialpermission } from "../../../redux/amcstatus";
import PermissionMatrix from "./PermissionMatrix";
import SpecialPermissions from "./SpecialPermissions";
import {
  AxAvatar,
  AxCaret,
  AxFact,
  AxField,
  AxPill,
  AxSectionHead,
  AxSwitchRow,
  AxTabs,
  AxTodoNote,
  AxTodoTag,
} from "../ui/adminUi";

// the four permission tabs, each with its endpoints and the indexes the old
// screens dropped from the special lists
const PERM_TABS = {
  webmenu: { label: "Web menu", kind: "menu", get: getwebmenupermission, save: savewebmenupermission },
  webspecial: {
    label: "Web special",
    kind: "special",
    get: getwebmenuspecialpermission,
    save: savewebspecialpermission,
    drop: [3, 7, 8, 9],
  },
  winmenu: {
    label: "Windows menu",
    kind: "menu",
    get: getwindowmenupermission,
    save: savewindowmenupermission,
  },
  winspecial: {
    label: "Windows special",
    kind: "special",
    get: getwindowmenuspecialpermission,
    save: savewindowspecialpermission,
    drop: [16, 17, 18],
  },
};
const PERM_KEYS = Object.keys(PERM_TABS);

const countMenu = (rows) =>
  (rows || []).reduce(
    (n, r) => n + (r.view ? 1 : 0) + (r.add ? 1 : 0) + (r.edit ? 1 : 0) + (r.deletion ? 1 : 0),
    0
  );
const countSpecial = (rows) => (rows || []).filter((r) => r?.userRole?.id !== 0).length;

const RoleDetail = ({ mode, data, levels, handleClose, onDelete, canDelete, canEdit = true }) => {
  const { register, handleSubmit, setValue, watch, reset } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const dispatch = useDispatch();
  const { userId } = useSelector((state) => state.userauth);
  const { UserServicelist } = useSelector((state) => state.userservice);

  const [tab, setTab] = useState("profile");
  const [perms, setPerms] = useState({}); // key -> rows
  const [loaded, setLoaded] = useState({});
  const [loading, setLoading] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const isNew = mode !== "edit";

  /* ---- load the role, exactly as the old modal did ---- */
  useEffect(() => {
    if (mode === "edit" && data) {
      setValue("name", data.name);
      setValue("level", data.level);
      setValue("active", data.active);
    } else {
      reset();
    }
    setTab("profile");
    setPerms({});
    setLoaded({});
    setTouched({});
    setMsg("");
  }, [data, mode]);

  /* ---- a permission tab fetches the first time you open it ---- */
  useEffect(() => {
    if (isNew || tab === "profile" || loaded[tab] || loading[tab]) return;
    const cfg = PERM_TABS[tab];
    if (!cfg) return;
    let cancelled = false;
    setLoading((l) => ({ ...l, [tab]: true }));
    (async () => {
      try {
        const response = await cfg.get(data);
        if (cancelled) return;
        let rows;
        if (cfg.kind === "menu") rows = response?.menuPermissions || [];
        else {
          const drop = cfg.drop || [];
          rows = (response?.userSpecialPermissionList || []).filter(
            (_, index) => !drop.includes(index)
          );
        }
        setPerms((p) => ({ ...p, [tab]: rows }));
        setLoaded((l) => ({ ...l, [tab]: true }));
      } catch (error) {
        console.error("Error fetching permissions:", error);
        if (!cancelled) {
          setPerms((p) => ({ ...p, [tab]: [] }));
          setLoaded((l) => ({ ...l, [tab]: true }));
        }
      } finally {
        if (!cancelled) setLoading((l) => ({ ...l, [tab]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, data, isNew]);

  const changePerms = (key, rows) => {
    setPerms((p) => ({ ...p, [key]: rows }));
    setTouched((t) => ({ ...t, [key]: true }));
    setMsg("");
  };

  /* ---- profile save: the old AdduserRole body, unchanged ---- */
  const handleFormSubmit = async (formData) => {
    const objRole = new UserRole();
    objRole.name = formData.name;
    objRole.Level = formData.level;
    objRole.Active = formData.active;
    objRole.updatedUser = `{${userId?.id}}`;
    if (mode === "edit") {
      objRole.guid = data.guid;
      objRole.id = data.id;
    }
    setSaving(true);
    try {
      const response =
        mode === "edit" ? await updateuserRole(objRole) : await adduserRole(objRole);

      if (response === true) {
        toast.success("Success");
        dispatch(fetchUserRole());
        setTouched((t) => ({ ...t, profile: false }));
        if (isNew) handleClose();
      } else {
        toast.error("Save failed");
      }
    } catch (error) {
      toast.error(error?.response?.data?.Message);
      console.error("Error handling Account Type:", error);
    } finally {
      setSaving(false);
    }
  };

  /* ---- permission saves: the old modals' bodies, unchanged ---- */
  const savePermissions = async () => {
    const cfg = PERM_TABS[tab];
    if (!cfg) return;
    const rows = perms[tab] || [];
    setSaving(true);
    try {
      if (cfg.kind === "menu") {
        const webObj = new WebUserMenuPermission();
        webObj.UserRole = data;
        webObj.MenuPermissions = rows;
        await cfg.save(webObj);
        toast.success("Permissions saved successfully!");
        if (tab === "webmenu") {
          dispatch(fetchMenuServices({ id: userId.id }));
          dispatch(fetchmenuPermissions(UserServicelist.userRole));
        }
      } else {
        const webObj = new UserSpecialPermission();
        webObj.UserRole = data;
        webObj.userSpecialPermissionList = rows.filter((e) => e?.userRole?.id !== 0);
        const response = await cfg.save(webObj);
        if (response == true) {
          toast.success("success");
          if (tab === "webspecial")
            dispatch(fetchspecialpermission(UserServicelist.userRole));
        }
      }
      setTouched((t) => ({ ...t, [tab]: false }));
    } catch (error) {
      toast.error("Failed to save permissions.");
      console.error("Error:", error);
    } finally {
      setSaving(false);
    }
  };

  const name = watch("name") || "";
  const level = watch("level");
  const active = watch("active");

  const tabs = [
    { key: "profile", label: "Profile", changed: !!touched.profile },
    ...PERM_KEYS.map((k) => ({
      key: k,
      label: PERM_TABS[k].label,
      count: loaded[k]
        ? PERM_TABS[k].kind === "menu"
          ? countMenu(perms[k])
          : countSpecial(perms[k])
        : undefined,
      changed: !!touched[k],
    })),
  ];

  const subBits = [
    "Level " + (level || "—"),
    active ? "Active" : "Inactive",
    isNew ? "Not saved yet" : "Permissions on the tabs",
  ];

  const isPermTab = tab !== "profile";
  const cfg = PERM_TABS[tab];

  return (
    <form
      className="axa-detailform"
      onSubmit={handleSubmit(handleFormSubmit)}
      onChange={() => {
        if (tab === "profile") setTouched((t) => ({ ...t, profile: true }));
      }}
    >
      {/* ============ HEADER ============ */}
      <div className="axa-dhead">
        <AxAvatar name={name || "new"} />
        <div className="axa-dtitle">
          <div className="t">
            <span className="nm">{name || (isNew ? "New role" : "Role")}</span>
          </div>
          <div className="s">{subBits.join(" · ")}</div>
        </div>
        <AxPill state={isNew ? "new" : touched[tab] ? "dirty" : "saved"} />
      </div>

      <AxTabs tabs={tabs} active={tab} onChange={setTab} />

      <div className={"axa-tabbody" + (isPermTab ? " flush" : "")}>
        {/* ---------- PROFILE ---------- */}
        <div className={"axa-pane" + (tab === "profile" ? " show" : "")}>
          <AxSectionHead>The role</AxSectionHead>
          <div className="axa-formgrid">
            <AxField label="Name" required>
              <input
                type="text"
                autoComplete="off"
                placeholder="e.g. Branch Supervisor"
                disabled={!canEdit}
                {...register("name")}
                required
              />
            </AxField>

            <AxField
              label="Level"
              required
              className="sel"
              hint="A higher level may approve and override lower ones."
            >
              <select disabled={!canEdit} {...register("level")}>
                <option value="">select</option>
                {levels?.map((obj) => (
                  <option value={obj} key={obj}>
                    {obj}
                  </option>
                ))}
                {mode == "edit" && (
                  <option value={data.level} key={data.level}>
                    {data.level}
                  </option>
                )}
              </select>
              <AxCaret />
            </AxField>

            <AxSwitchRow
              label={active ? "Active" : "Inactive"}
              note="An inactive role keeps its permissions but nobody signs in with it."
              disabled={!canEdit}
              {...register("active")}
            />

            <AxField label="Description" full todo>
              <textarea
                disabled
                placeholder="Who this role is for, in one line — shown when assigning users"
              />
            </AxField>
          </div>

          <AxSectionHead>At a glance</AxSectionHead>
          <div className="axa-facts">
            <AxFact k="Level" v={level || "—"} />
            <AxFact k="Status" v={active ? "Active" : "Inactive"} />
            <AxFact
              k="Web granted"
              v={loaded.webmenu ? countMenu(perms.webmenu) : "—"}
              s={loaded.webspecial ? countSpecial(perms.webspecial) + " special" : ""}
            />
            <AxFact
              k="Windows granted"
              v={loaded.winmenu ? countMenu(perms.winmenu) : "—"}
              s={loaded.winspecial ? countSpecial(perms.winspecial) + " special" : ""}
            />
          </div>

          <AxTodoNote>
            Two things the mock-up shows here have nowhere to live yet: the role&apos;s
            description, and the count of users assigned to it. See
            docs/BACKEND-COMPLETE.md (Part 3).
          </AxTodoNote>
        </div>

        {/* ---------- PERMISSION TABS ---------- */}
        {PERM_KEYS.map((key) => (
          <div className={"axa-pane" + (tab === key ? " show" : "")} key={key}>
            {isNew ? (
              <div className="axa-emptyperm">
                Save the role first — permissions are granted to a role that exists.
              </div>
            ) : PERM_TABS[key].kind === "menu" ? (
              <PermissionMatrix
                rows={perms[key] || []}
                loading={!!loading[key]}
                onChange={(rows) => changePerms(key, rows)}
              />
            ) : (
              <SpecialPermissions
                items={perms[key] || []}
                loading={!!loading[key]}
                roleId={data?.id}
                onChange={(rows) => changePerms(key, rows)}
              />
            )}
          </div>
        ))}
      </div>

      {/* ============ FOOTER ============ */}
      <div className="axa-dfoot">
        <span className={"msg" + (msg ? " err" : "")}>
          {msg ||
            (isPermTab && !isNew
              ? "Each permission screen saves on its own — this button saves the tab you are on."
              : "")}
        </span>
        <span className="sp" />
        {mode === "edit" && canDelete ? (
          <button
            type="button"
            className="axa-btn axa-btn-danger"
            onClick={() => onDelete && onDelete(data)}
          >
            Delete
          </button>
        ) : null}
        <button type="button" className="axa-btn axa-btn-ghost" onClick={handleClose}>
          {isNew ? "Cancel" : "Close"}
        </button>
        {isPermTab ? (
          <button
            type="button"
            className="axa-btn axa-btn-primary"
            disabled={isNew || saving || !loaded[tab]}
            onClick={savePermissions}
          >
            {saving ? "Saving…" : "Save " + (cfg ? cfg.label.toLowerCase() : "")}
          </button>
        ) : (
          <button type="submit" className="axa-btn axa-btn-primary" disabled={saving || !canEdit}>
            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Save"}
          </button>
        )}
      </div>
    </form>
  );
};

export default RoleDetail;
